import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { PartService } from '../../../service/api/part-service';
import { InspectionPlanService } from '../../../service/api/inspectionplan-service';

const { QUALITYALERT_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'qualityalert';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
    status: {
      value: 'In Production',
      label: 'In Production',
    },
  },
  qualityAlertLoading: false,
  filterVisible: true,
  tileView: false,
  selectedQualityAlert: null,
  editModalVisible: false,
  qualityAlertsData: {},
};

export const fetchQualityAlerts = createAsyncThunk(
  'qualityAlert/fetchQualityAlerts',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await PartService.getAllParts(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to load Quality Alert Pages',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load  QualityAlert Pages');
    }
  },
);

export const editQualityAlert = createAsyncThunk(
  'qualityAlert/editQualityAlert',
  async (data, { rejectWithValue }) => {
    try {
      const response = await PartService.getPartDetailsById(data);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to update Quality Alert Pages',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update Quality Alert Pages');
    }
  },
);

export const editQualityAlertData = createAsyncThunk(
  'qualityAlert/editQualityAlertData',
  async (partID, { rejectWithValue }) => {
    try {
      const response = await PartService.getPartDetailsById(partID);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to load Quality Alert Pages data',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Quality Alert Pages data');
    }
  },
);

export const addQualityAlert = createAsyncThunk(
  'qualityAlert/addQualityAlert',
  async (inspectionPlanPayload, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.createQualityAlert(inspectionPlanPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to Save Quality Alert');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to Save Quality Alert');
    }
  },
);

export const fetchQualityAlertByPartId = createAsyncThunk(
  'qualityAlert/fetchQualityAlertByPartId',
  async (partID, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.getQualityAlertsByPartId(partID);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to load Quality Alert data',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Quality Alert data');
    }
  },
);
export const deleteQualityAlert = createAsyncThunk(
  'qualityAlert/deleteQualityAlert',
  async (alertId, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.deleteQualityAlertById(alertId);
      if (response.status === 200) {
        return alertId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete Quality Alert');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete Quality Alert');
    }
  },
);

export const fetchQualityAlertWithFilters = () => (dispatch, getState) => {
  const filters = getState().qualityalert.filters;
  dispatch(
    fetchQualityAlerts({
      status: filters?.status?.value,
    }),
  );
};

export const { reducer, actions } = createSlice({
  name: 'qualityalert',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchQualityAlerts.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQualityAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchQualityAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(editQualityAlert.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editQualityAlert.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editQualityAlertData.pending, state => {
        state.qualityAlertLoading = true;
      })
      .addCase(editQualityAlertData.fulfilled, state => {
        state.qualityAlertLoading = false;
      })
      .addCase(editQualityAlertData.rejected, state => {
        state.qualityAlertLoading = false;
      })
      .addCase(addQualityAlert.fulfilled, (state, action) => {
        const newMold = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newMold, ...state.data];
      })
      .addCase(addQualityAlert.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteQualityAlert.fulfilled, (state, action) => {
        state.data = state.data.filter(alert => alert.id !== action.payload);
      })
      .addCase(deleteQualityAlert.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(fetchQualityAlertByPartId.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(fetchQualityAlertByPartId.fulfilled, (state, action) => {
        state.qualityAlertsData = action.payload;
        state.loading = false;
      })
      .addCase(fetchQualityAlertByPartId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editQualityAlert.rejected, editQualityAlertData.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addQualityAlert.fulfilled, editQualityAlert.fulfilled),
  effect: (action, listenerApi) => {
    // listenerApi.dispatch(closeModal(QUALITYALERT_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Quality Alert Success',
        type: 'success',
      }),
    );
    listenerApi.dispatch(fetchQualityAlertWithFilters());
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteQualityAlert.fulfilled),
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Quality Alert deleted successfully.',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editQualityAlertData.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(QUALITYALERT_ADD_EDIT, action.payload));
  },
});

redux.injectReducer(STORE_KEY, reducer);
